import axios from 'axios';
import { resolveApiBaseUrl } from './resolveApiBaseUrl';
import { attachAuth, noteItemProject, getItemProject } from './authToken';
// #168 WS2: billing 402/403 envelopes render app-wide (mapped onto .detail).
// #970: this import is STATIC and must stay static. It used to be
// `import('./billingApi').then(...)`, which installed the interceptor on a
// microtask — after this module finished evaluating — so any request issued in
// that window went out unmapped. billingApi.js imports only axios,
// ./resolveApiBaseUrl and ./authToken, so there is no cycle for a dynamic
// import to break, and iam/billing.jsx already imports it statically, so there
// is no chunk to split either (Rollup said so: INEFFECTIVE_DYNAMIC_IMPORT).
import { installBillingEnvelopeInterceptor } from './billingApi';

// API base URL (always ends in `/api`). See resolveApiBaseUrl.js for the
// precedence: explicit REACT_APP_API_BASE_URL > Codespaces origin derivation >
// same-origin fallback.
export const API_BASE_URL = resolveApiBaseUrl();

// #161: `/health` is mounted at the backend's ROOT (main.py `@app.get("/health")`),
// not under `/api` — same origin as API_BASE_URL, just without the `/api` suffix.
// This matches the live-deploy convention documented in CLAUDE.md ("Health = API
// origin root .../health") for both dev and prod, where frontend and backend are
// different hosts.
export const API_ROOT_URL = API_BASE_URL.replace(/\/api\/?$/, '');

console.log('API_BASE_URL:', API_BASE_URL); // Debug log to verify the resolved value

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Attach the bearer token + active-org header to every core-API request (SAAS.3.1).
api.interceptors.request.use(attachAuth);

// Add request/response interceptors for debugging
api.interceptors.request.use(request => {
    // CONF3: do not log headers — they may carry the user's X-LLM-API-Key.
    console.log('API Request:', {
        url: request.url,
        method: request.method,
        data: request.data
    });
    return request;
});

api.interceptors.response.use(
    response => {
        console.log('API Response:', {
            url: response.config.url,
            status: response.status,
            data: response.data
        });
        return response;
    },
    error => {
        console.error('API Error:', {
            url: error.config?.url,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
        });
        return Promise.reject(error);
    }
);

// #168 WS2 / #970: billing 402/403 envelopes render app-wide (mapped onto .detail).
// Registered HERE, after the debug response interceptor, on purpose — axios walks
// response interceptors in registration order, and the old dynamic import resolved
// after this module finished evaluating, so billing was always the SECOND response
// interceptor. Calling it any earlier in this file would silently reorder the chain.
installBillingEnvelopeInterceptor(api);

export const requirementsApi = {
    // List the projects (root-level items) for the active org, for pickers like the
    // Vector Space project selector. Each: { id, title, is_container, child_count }.
    async getProjects() {
        const response = await api.get('/projects');
        return response.data.data || [];
    },

    // Embedding maintenance (admin): embed only what's missing, or clear + rebuild all.
    async fixEmbeddings() {
        return (await api.post('/analysis/embeddings/fix')).data;
    },
    async recreateEmbeddings() {
        return (await api.post('/analysis/embeddings/recreate')).data;
    },

    // `all=false` is the LAZY path (root level only, each node annotated with
    // has_children); `all=true` returns the full eager tree (legacy/compat). Pass a
    // projectId to scope this single request to one project (X-Project-Id), which lets
    // the tree fetch each visible project's level independently for grouping.
    async getItems(all = true, projectId = null, opts = {}) {
        try {
            // #349: include_archived surfaces soft-deleted (archived) items so the tree's
            // "Show archived" filter can display + restore them.
            const params = { all_items: all };
            if (opts && opts.includeArchived) params.include_archived = true;
            // #380: the lazy level is paginated so a huge flat project returns a BOUNDED
            // payload (unbounded ones timed out / reset the connection). Pass limit/offset
            // to window it; pass `paged: true` to also get back the { has_more, total,
            // next_offset } page metadata (default return stays the bare array).
            if (opts && opts.limit != null) params.limit = opts.limit;
            if (opts && opts.offset != null) params.offset = opts.offset;
            const response = await api.get(`/items`, {
                params,
                ...(projectId ? { headers: { 'X-Project-Id': projectId } } : {}),
            });

            console.log('Tree structure data:', response.data.data);

            const items = response.data.data || [];
            if (opts && opts.paged) return { items, page: response.data.page || null };
            // Data already contains hierarchical structure with children
            return items;
        } catch (error) {
            console.error('Failed to fetch items:', error);
            throw error;
        }
    },

    // #428: bounded project aggregates for DashboardHome / ProjectDetails — KPI
    // counts, per-area coverage, recent items, capped triage lists (+ full id sets
    // for Verify-all/Archive-all) — replacing those views' all_items=true
    // whole-tree loads.
    async getItemsSummary(projectId = null) {
        try {
            const response = await api.get(`/items/summary`, {
                timeout: 20000, // cold-start rationale as searchItems below
                ...(projectId ? { headers: { 'X-Project-Id': projectId } } : {}),
            });
            return response.data.data;
        } catch (error) {
            console.error('Failed to fetch items summary:', error);
            throw error;
        }
    },

    // #428: server-side review-state filter — same pruned { roots, expand,
    // match_ids, truncated } shape as searchItems, matches = the items in the
    // given review states — so the tree's review filter no longer eager-loads
    // whole project trees client-side.
    async getReviewFilterTree(states, projectId = null, opts = {}) {
        try {
            const params = { states: (states || []).join(',') };
            if (opts && opts.includeArchived) params.include_archived = true;
            const response = await api.get(`/items/review-filter`, {
                params,
                timeout: 20000, // cold-start rationale as searchItems below
                ...(projectId ? { headers: { 'X-Project-Id': projectId } } : {}),
            });
            return response.data.data || { roots: [], expand: [], match_ids: [], truncated: false };
        } catch (error) {
            console.error('Failed to fetch review-filter tree:', error);
            throw error;
        }
    },

    // Lazy-tree text search: returns { roots, expand, match_ids, truncated } — a pruned
    // tree of matches plus the ancestor scaffolding, so filtering spans the whole
    // project (not just the loaded nodes). Optionally scoped to one project.
    async searchItems(query, projectId = null) {
        try {
            const response = await api.get(`/items/search`, {
                params: { q: query },
                // #201 follow-up: this call had no timeout at all, so a slow/cold-starting
                // backend (e.g. a spun-down Render free-tier dyno) left the caller's
                // "Searching…" state pending indefinitely with no eventual error — reading
                // exactly like "no results are ever displayed" on mobile. 20s comfortably
                // covers a cold start while still surfacing a real failure eventually.
                timeout: 20000,
                ...(projectId ? { headers: { 'X-Project-Id': projectId } } : {}),
            });
            return response.data.data || { roots: [], expand: [], match_ids: [], truncated: false };
        } catch (error) {
            console.error('Failed to search items:', error);
            throw error;
        }
    },

    // #150: fast flat id+title search for the command palette — GET /search/quick.
    // Returns a short, capped list (no tree/ancestor scaffolding), so it's cheap
    // enough to call on every keystroke.
    async quickSearch(query, limit = 8, offset = 0) {
        try {
            const response = await api.get(`/search/quick`, {
                params: { q: query, limit, offset },
                timeout: 20000, // same cold-start rationale as searchItems above
            });
            return response.data.data || [];
        } catch (error) {
            console.error('Failed to quick-search items:', error);
            throw error;
        }
    },

    // Semantic (vector) search for the command palette's Vector mode (#150). Maps the
    // chat tool's rich requirement dicts down to the SAME flat row shape quickSearch
    // returns, so the palette renders both modes uniformly. An empty list is a valid
    // "no semantic matches" result — note Vector results depend on the semantic index
    // being populated (see #223: dev's index is empty until the embedding dim is aligned).
    async vectorSearch(query, limit = 8) {
        try {
            const response = await api.post('/chat/tools/vectorsearch_requirements',
                { query, limit }, { timeout: 20000 });
            const reqs = (response.data && response.data.requirements) || [];
            return reqs.map((r) => ({
                id: r.id, display_id: r.display_id, title: r.title, type_id: r.type_id,
                project_id: r.project_id,
            }));
        } catch (error) {
            console.error('Failed to vector-search items:', error);
            throw error;
        }
    },

    // Ancestor ids (root → immediate parent) so the lazy tree can open a branch
    // straight to a deep-linked / selected item.
    async getAncestors(itemId, { unscoped = false } = {}) {
        try {
            // #917: `unscoped` drops X-Project-Id for this one request. A deep link names
            // an item and not its project, and the project cannot be discovered from
            // inside the viewer's current scope — the backend resolves this endpoint
            // through the tenancy-scoped manager, so a cross-project item comes back with
            // an empty ancestor list and a null project_id rather than the real one.
            const response = await api.get(`/items/${itemId}/ancestors`, { rmdvUnscopedProject: unscoped });
            // #396/#380: also carry the item's project id so the tree can prepend the
            // synthetic project group node when the org is multi-project.
            return {
                ancestors: response.data.data || [],
                projectId: response.data.project_id || null,
            };
        } catch (error) {
            console.error('Failed to fetch ancestors:', error);
            return { ancestors: [], projectId: null };
        }
    },

    // #511: default behaviour (unchanged, and still covered by api.test.js /
    // services/__tests__/api.test.js) is to swallow a fetch failure and return
    // a safe "Error loading item" placeholder so most read-only views always
    // have something renderable. That's fine for a display-only read, but it's
    // wrong for a caller that might later PUT this data back — the synthetic
    // placeholder ("title: 'Error loading item', description: 'Could not
    // load...'") looks like real data and can get silently saved over the
    // genuine server record. `{ throwOnError: true }` opts a write-capable
    // caller (ItemCardWindow) OUT of the placeholder so it gets a real
    // rejection to react to instead.
    async getItem(id, { throwOnError = false } = {}) {
        try {
            // Debug logging
            console.log(`Fetching item with ID: ${id}`);

            const response = await api.get(`/items/${id}`);
            console.log('Item API Response:', {
                url: response.config.url,
                status: response.status,
                data: response.data
            });

            return response.data.data;
        } catch (error) {
            // #917: a deep link names the ITEM and not its project, so this request
            // carried the viewer's stored project as X-Project-Id and 404'd. The tree's
            // openToItem resolves the project before selecting, but `/item/:id` renders
            // ItemDetails directly (Dashboard.js) and the item-card's open-full-page
            // button goes the same way — neither passes through the tree. So resolve it
            // HERE and retry once, which covers every caller of getItem.
            //
            // Deliberately in the API layer, not a React effect: re-running ItemDetails'
            // load effect on a project change would also un-pin the tab, discarding one
            // the user just clicked (#801, and the reason that approach was reverted).
            // getAncestors is not project-scoped and swallows its own errors, returning
            // projectId null — so a genuine 404 falls straight through to the handling
            // below. `getItemProject(id)` already being set means the note was tried and
            // did not help, which bounds this to a single retry and cannot recurse.
            if (error?.response?.status === 404 && !getItemProject(id)) {
                const { projectId } = await requirementsApi.getAncestors(id, { unscoped: true });
                if (projectId) {
                    noteItemProject(id, projectId);
                    try {
                        const retried = await api.get(`/items/${id}`);
                        return retried.data.data;
                    } catch (retryError) {
                        console.error('Failed to fetch item details after resolving its project:', retryError);
                        if (throwOnError) throw retryError;
                        return {
                            id: id,
                            title: 'Error loading item',
                            description: `Could not load item details: ${retryError.message}`,
                            attributes: {}
                        };
                    }
                }
            }
            console.error('Failed to fetch item details:', error);
            if (throwOnError) throw error;
            // Return a default item structure to prevent UI errors
            return {
                id: id,
                title: 'Error loading item',
                description: `Could not load item details: ${error.message}`,
                attributes: {}
            };
        }
    },

    async createItem(item) {
        try {
            console.log('Creating new item:', item);
            
            // Validate required fields
            if (!item.title) {
                throw new Error('Title is required');
            }
            
            // Prepare the item data for API
            const itemData = {
                title: item.title,
                description: item.description || '',
                attributes: {
                    ...item.attributes
                }
            };

            // Ensure container type is properly set
            if (item.is_container || item.is_folder) {
                itemData.attributes.type = 'container';
            }

            // #197: pass through an optional custom item type (key or id) so it's
            // resolved server-side instead of always defaulting to the system
            // requirement/container type — e.g. from AddDialog's type selector.
            if (item.type) {
                itemData.type = item.type;
            }
            if (item.type_id) {
                itemData.type_id = item.type_id;
            }

            // Add parent ID if provided
            const config = {};
            if (item.parent_id) {
                config.params = {
                    parent_id: item.parent_id
                };
            }
            // #287: a TOP-LEVEL create (no parent_id) resolves its target project
            // ONLY from the X-Project-Id header server-side (resolve_target_project
            // never reads the body) — so an explicit caller-resolved project (e.g.
            // AddDialog, when invoked from a specific project's own context menu)
            // must ride as a per-request header, not a body field.
            if (item.project_id) {
                config.headers = { 'X-Project-Id': item.project_id };
            }

            // Make API call
            const response = await api.post('/items', itemData, config);
            
            // Debug log the response
            console.log('Create item response:', response.data);
            
            if (!response.data || !response.data.data) {
                throw new Error('Invalid response format when creating item');
            }
            
            // Return the created item
            return response.data.data;
        } catch (error) {
            // Enhance error message for user feedback
            const errorMessage = error.response?.data?.detail || error.message || 'Failed to create item';
            console.error('Failed to create item:', errorMessage);
            throw new Error(errorMessage, { cause: error });
        }
    },

    async updateItem(id, item) {
        try {
            console.log(`Updating item ${id}:`, item);
            const response = await api.put(`/items/${id}`, item);
            return response.data.data;
        } catch (error) {
            console.error('Failed to update item:', error);
            throw error;
        }
    },

    /**
     * Re-parent and/or reorder an item in the hierarchy (tree-grid drag-and-drop).
     * @param {string} id - item being moved
     * @param {{ new_parent_id?: string|null, sibling_order?: string[] }} payload
     */
    async moveItem(id, payload) {
        try {
            const response = await api.put(`/items/${id}/move`, payload);
            return response.data.data;
        } catch (error) {
            console.error('Failed to move item:', error);
            throw error;
        }
    },

    async getHistory(itemId) {
        try {
            const response = await api.get(`/items/${itemId}/history`);
            return response.data.data;
        } catch (error) {
            console.error('Failed to load item history:', error);
            throw error;
        }
    },

    async getHistoryVersion(itemId, version) {
        try {
            const response = await api.get(`/items/${itemId}/history/${version}`);
            return response.data.data;
        } catch (error) {
            console.error('Failed to load item history version:', error);
            throw error;
        }
    },

    async restoreVersion(itemId, version) {
        try {
            const response = await api.post(`/items/${itemId}/history/${version}/restore`);
            return response.data.data;
        } catch (error) {
            console.error('Failed to restore item version:', error);
            throw error;
        }
    },

    async getActivity(limit = 20) {
        try {
            const response = await api.get(`/activity`, { params: { limit } });
            return response.data.data;
        } catch (error) {
            console.error('Failed to load activity feed:', error);
            throw error;
        }
    },

    // #441: optional projectId scopes the analysis to one project (X-Project-Id,
    // mirroring getItemsSummary) so the dashboard's lazy-loaded metric detail
    // matches the header count for the SELECTED project — not the whole org.
    async getCoverage(projectId = null) {
        try {
            const response = await api.get(`/coverage`, {
                ...(projectId ? { headers: { 'X-Project-Id': projectId } } : {}),
            });
            return response.data.data;
        } catch (error) {
            console.error('Failed to load coverage analysis:', error);
            throw error;
        }
    },

    async getQuality(projectId = null) {
        try {
            const response = await api.get(`/quality`, {
                ...(projectId ? { headers: { 'X-Project-Id': projectId } } : {}),
            });
            return response.data.data;
        } catch (error) {
            console.error('Failed to load quality overview:', error);
            throw error;
        }
    },

    // #449: the dashboard's "Links by type" chip -> docked drawer. Paginated —
    // the same { limit, offset } window as getItems — scoped to one project like
    // getCoverage/getQuality (X-Project-Id). Returns { items, page } where `items`
    // is the RequirementDTO-shaped row list and `page` carries
    // { limit, offset, returned, total, has_more, next_offset }.
    async getItemsByLinkType(linkType, { projectId = null, limit = 20, offset = 0 } = {}) {
        try {
            const response = await api.get(`/items/by-link-type`, {
                params: { type: linkType, limit, offset },
                ...(projectId ? { headers: { 'X-Project-Id': projectId } } : {}),
            });
            return { items: response.data.data || [], page: response.data.page || null };
        } catch (error) {
            console.error('Failed to load items by link type:', error);
            throw error;
        }
    },

    async exportMarkdown(itemId) {
        try {
            const response = await api.get(`/items/${itemId}/export/markdown`);
            return response.data.data;
        } catch (error) {
            console.error('Failed to export markdown:', error);
            throw error;
        }
    },

    // Export a container (and its descendants) as a Markdown document. Returns
    // the `{ id, format, content }` payload. Alias used by the Document tab; the
    // explicit name mirrors getItem/getHistory for readability at call sites.
    exportItemMarkdown(itemId) {
        return this.exportMarkdown(itemId);
    },

    // #341: structured subtree JSON ({ id, format, subtree }) — the Document
    // tab's render source (id chips / type chips / attribute tables) and the
    // client-side Excel(CSV)/print exports.
    async exportItemSubtree(itemId) {
        try {
            const response = await api.get(`/items/${itemId}/export/json`);
            return response.data.data;
        } catch (error) {
            console.error('Failed to export subtree JSON:', error);
            throw error;
        }
    },

    // #341: minimal valid ReqIF 1.2 XML of the subtree ({ id, format, content }).
    async exportItemReqif(itemId) {
        try {
            const response = await api.get(`/items/${itemId}/export/reqif`);
            return response.data.data;
        } catch (error) {
            console.error('Failed to export ReqIF:', error);
            throw error;
        }
    },

    // #381 follow-up: Word (.docx) — real binary download (responseType 'blob';
    // the server streams the bytes off the event loop, no base64+JSON envelope).
    // Resolves to the Blob itself.
    async exportItemDocx(itemId) {
        try {
            const response = await api.get(`/items/${itemId}/export/docx`, { responseType: 'blob' });
            return response.data;
        } catch (error) {
            console.error('Failed to export Word:', error);
            throw error;
        }
    },

    // #381 follow-up: Excel (.xlsx) — genuine multi-worksheet workbook, binary.
    async exportItemXlsx(itemId) {
        try {
            const response = await api.get(`/items/${itemId}/export/xlsx`, { responseType: 'blob' });
            return response.data;
        } catch (error) {
            console.error('Failed to export Excel:', error);
            throw error;
        }
    },

    // Breakdown / review-feedback tab (#342). `comments_api.py` is a NEW router
    // (not the legacy `/items` envelope this file's older methods unwrap via
    // `response.data.data`) — it returns raw JSON, the same convention as
    // notificationsApi.js. NOTE: the payload-shaping endpoint lives at
    // `/breakdown`, NOT `/review` — `/items/{id}/review` is an existing,
    // unrelated AI quality-review endpoint (see api.py's `review_requirement` /
    // test_ai_review.py) and would have silently collided.
    async getReview(itemId) {
        try {
            const response = await api.get(`/items/${itemId}/breakdown`);
            return response.data;
        } catch (error) {
            console.error('Failed to load review payload:', error);
            throw error;
        }
    },

    // #669: the approval trail's tamper-evidence verdict for ONE item —
    // `{ok, events, broken_at}`. The route (#630) has existed since PR #639 but
    // nothing in the UI called it, so a broken chain was discoverable only by an
    // auditor who already suspected something AND knew the endpoint existed. That
    // is the gap #630 was narrowed to rather than closed.
    //
    // Returns `{ok: null}` on ANY failure instead of throwing. Deliberate: the
    // caller renders a verdict beside an approval record, and a verdict that
    // cannot be obtained must read as INDETERMINATE, never as "verified". The
    // route 404s for an unknown or out-of-scope item, which must not be mistaken
    // for a clean chain either — so every non-success collapses to "unknown", and
    // only an explicit `ok: false` means "broken".
    async verifyApprovalTrail(itemId) {
        try {
            const response = await api.get(`/items/${itemId}/approval-trail/verify`);
            const d = response.data || {};
            return (typeof d.ok === 'boolean')
                ? { ok: d.ok, events: d.events, broken_at: d.broken_at || [] }
                : { ok: null };
        } catch (error) {
            console.error('Failed to verify approval trail:', error);
            return { ok: null };
        }
    },

    // #421: O(1) `{count, open}` for the Comments tab badge — replaces seeding
    // the badge from getReview()/`/breakdown`, which walked the item's whole
    // subtree on every item select. Raw-JSON convention like getReview.
    async getCommentCount(itemId) {
        try {
            const response = await api.get(`/items/${itemId}/comments/count`);
            return response.data;
        } catch (error) {
            console.error('Failed to load comment count:', error);
            throw error;
        }
    },

    // `state`: 'approved' | 'not_approved' | null (null clears back to pending).
    // The reviewer is resolved server-side from auth — never sent by the client.
    //
    // F-06 / REQ-6096 (#603, D-09): `reason` is MANDATORY on 'not_approved' — the core
    // raises `ReasonRequired` and the route returns 400 without it, so a client that
    // omits it cannot reject at all.
    // F-08 / REQ-6098 (#603, D-08): `justification` is mandatory when the approval would
    // supersede another reviewer's OPEN rejection (400 without it; 403 without the
    // `approver` role). Both are passed through only when supplied rather than defaulted
    // to '': sending an empty string would convert "you must give a reason" into a stored
    // empty reason, which is exactly the unactionable record F-06 exists to prevent.
    /**
     * @param {string} itemId
     * @param {string} state
     * @param {{ reason?: string, justification?: string }} [extras]
     */
    async setApproval(itemId, state, { reason, justification } = {}) {
        try {
            const body = { state };
            if (reason !== undefined) body.reason = reason;
            if (justification !== undefined) body.justification = justification;
            const response = await api.put(`/items/${itemId}/approval`, body);
            return response.data;
        } catch (error) {
            console.error('Failed to set approval:', error);
            throw error;
        }
    },

    // #569: batch approval-state ROLLUP for the tree dots + item-detail badge —
    // { id: { state: 'approved'|'not_approved'|'stale'|'pending', mine: 'approved'|
    // 'not_approved'|null } }. Cross-tenant/unknown ids are simply absent from the
    // response (no 404/403 — see comments_api.py). `ids` accepts an array or an
    // already-joined string; an empty list short-circuits to {} without a request.
    //
    // N-20 / REQ-6132 (#595): CHUNKED, and body-transported once the id set is big.
    // The server no longer truncates — an over-cap request is a 413 — so the chunking
    // lives here, where the caller's full answer can still be assembled from several
    // bounded requests. Two reasons a single request is not enough:
    //   1. the server's documented per-request cap (comments_api.MAX_APPROVAL_STATE_IDS,
    //      500 — mirrored below), and
    //   2. a measured transport ceiling: a real uvicorn refuses a query string of
    //      ~1,986 32-char ids with an opaque `400 Invalid HTTP request received.`
    //      before any handler runs. Intermediaries on the hosted path may bind lower
    //      (not measured), so anything past a conservative URL budget goes as POST
    //      with a JSON body instead — same route, same response, no URL ceiling.
    async getApprovalStates(ids) {
        const list = Array.isArray(ids) ? ids : String(ids || '').split(',');
        const clean = list.map((x) => String(x || '').trim()).filter(Boolean);
        if (!clean.length) return {};
        // Mirrors comments_api.MAX_APPROVAL_STATE_IDS. Kept deliberately equal (not
        // "a bit under"): a chunk at exactly the cap is accepted, cap+1 is the 413.
        const CHUNK = 500;
        // Conservative query-string budget. The MEASURED server limit is far higher
        // (~65 KB of request head), but 2,000 characters is the long-standing
        // interop floor for intermediaries, and an over-long URL fails opaquely at
        // the transport rather than with a readable error — so cross it deliberately
        // into POST rather than discovering it in production.
        const URL_BUDGET = 1800;
        const chunks = [];
        for (let i = 0; i < clean.length; i += CHUNK) chunks.push(clean.slice(i, i + CHUNK));
        try {
            const parts = await Promise.all(chunks.map(async (chunk) => {
                const joined = chunk.join(',');
                const response = joined.length <= URL_BUDGET
                    ? await api.get('/items/approval-states', { params: { ids: joined } })
                    : await api.post('/items/approval-states', { ids: chunk });
                return response.data || {};
            }));
            return Object.assign({}, ...parts);
        } catch (error) {
            console.error('Failed to load approval states:', error);
            throw error;
        }
    },

    // D-06 / #582 (v1): the DERIVED, type-scoped verification state —
    // `{ id: { state: 'verified'|'unverified'|null, basis } }`. GET-only, no
    // 413/chunk dance like getApprovalStates above: `/items/verification-states`
    // truncates an over-cap id set rather than rejecting it (comments_api's cap
    // semantics don't apply here), and this is called with a handful of ids at a
    // time (the item-detail page's own item), so one request is enough.
    async getVerificationStates(ids) {
        const list = Array.isArray(ids) ? ids : String(ids || '').split(',');
        const clean = list.map((x) => String(x || '').trim()).filter(Boolean);
        if (!clean.length) return {};
        try {
            const response = await api.get('/items/verification-states', {
                params: { ids: clean.join(',') },
            });
            return response.data || {};
        } catch (error) {
            console.error('Failed to load verification states:', error);
            throw error;
        }
    },

    // #678 v2: explicit verification EVIDENCE records — method/result/evidence per
    // item, layered on top of the DERIVED tri-state above. `evidence` is optional free
    // text (a verifying test_case id, a run/report reference, an attachment ref).
    /**
     * @param {string} itemId
     * @param {{ method?: string, result?: string, evidence?: string }} [record]
     */
    async addVerificationRecord(itemId, { method, result, evidence } = {}) {
        try {
            const body = { method, result };
            if (evidence !== undefined && evidence !== '') body.evidence = evidence;
            const response = await api.post(`/items/${itemId}/verification-records`, body);
            return response.data;
        } catch (error) {
            console.error('Failed to add verification record:', error);
            throw error;
        }
    },

    // Every evidence record for one item, newest first, each carrying `stale`
    // (recomputed server-side against the item's CURRENT content hash on every read).
    async getVerificationRecords(itemId) {
        try {
            const response = await api.get(`/items/${itemId}/verification-records`);
            return (response.data && response.data.data) || [];
        } catch (error) {
            console.error('Failed to load verification records:', error);
            throw error;
        }
    },

    // opts: { parentCommentId?, kind?, anchor? } — kind defaults server-side to
    // 'item'; pass kind:'overall' for the review-footer's "Complete review"
    // comment, parentCommentId for a reply, anchor for per-field anchoring.
    async addComment(itemId, body, opts = {}) {
        try {
            const { parentCommentId, kind, anchor } = opts || {};
            const response = await api.post(`/items/${itemId}/comments`, {
                body,
                ...(parentCommentId ? { parent_comment_id: parentCommentId } : {}),
                ...(kind ? { kind } : {}),
                ...(anchor ? { anchor } : {}),
            });
            return response.data;
        } catch (error) {
            console.error('Failed to add comment:', error);
            throw error;
        }
    },

    // N-21 / REQ-6133 (#609): the live-refresh CHANGE PROBE. One cheap indexed
    // aggregate returning `{marker, approvals, comments}`. `marker` is OPAQUE — compare
    // it for equality and nothing else; it is not a clock, and its format is explicitly
    // outside the API compatibility guarantee (docs/API_COMPATIBILITY_POLICY.md) so the
    // polling implementation can be swapped for a broker without a version bump.
    //
    // This is what makes "two reviewers on the same item see each other's decisions"
    // affordable: the steady-state cost of not being stale is this probe, not a
    // breakdown-sized refetch every tick.
    //
    // #697: the APP no longer calls this — every consumer goes through the shared
    // poller and `getReviewActivityBatch` below. It is kept because the single-project
    // route is published API (and the v1 mirror is versioned), so the client method
    // that exercises it stays too. Do not re-wire a component onto it: one caller is
    // all it takes to reintroduce the per-project fan-out.
    async getReviewActivity(projectId) {
        const response = await api.get(`/projects/${projectId}/review-activity`);
        return response.data;
    },

    // #697: the BATCH form of the probe above — every watched project in ONE request.
    //
    // The per-project form cost O(projects) requests per 10s tick, and the tree view
    // watches every membership from a view the shell never unmounts, so a user in 12
    // projects generated ~72 requests/minute before a second mount site doubled it.
    // That is what made dev unusable (#697); this collapses a tick to one request.
    //
    // POST for a read, deliberately: the id set is the input and a project-id list does
    // not belong in a URL (same reasoning as the approval-states batch, which measured
    // the real transport ceiling). It mutates nothing and is safe to retry.
    //
    // Returns `{activity: {pid: {marker, approvals, comments}}, unauthorized: [pid]}`.
    // Ids the caller may not watch come back NAMED in `unauthorized` rather than simply
    // missing — an absent key would be read as "nothing changed", which is a client
    // sitting on stale state while believing it is current.
    async getReviewActivityBatch(projectIds) {
        const ids = (projectIds || []).filter(Boolean);
        if (!ids.length) return { activity: {}, unauthorized: [] };
        const response = await api.post('/projects/review-activity', { project_ids: ids });
        return response.data;
    },

    // Which ITEMS carry an open comment, across MANY projects, in ONE request.
    //
    // Replaces the tree's `Promise.all(ids.map(getProjectFeedback))` fan-out — the
    // second review storm the owner hit on dev (2026-07-28), identical in shape to the
    // probe fan-out #697 removed and sitting three lines below it in the same file.
    // Narrower than the route it replaces on purpose: the dots need item ids, not
    // comment bodies (the single route returns up to 100 whole comment rows per
    // project, so the tree was fetching ~1200 rows of prose to draw a dozen dots).
    //
    // POST because the id set is the input (same reasoning as the approval-states and
    // review-activity batches). It mutates nothing and is safe to retry.
    //
    // Returns `{items: {pid: {item_uuids, truncated}}, unauthorized: [pid]}`. Ids the
    // caller may not read come back NAMED rather than missing — an absent key would be
    // read as "no open comments here", i.e. dots silently absent from a project.
    async getOpenCommentItemsBatch(projectIds, opts = {}) {
        const ids = (projectIds || []).filter(Boolean);
        if (!ids.length) return { items: {}, unauthorized: [] };
        const { status = 'open' } = opts || {};
        const response = await api.post('/projects/open-comment-items', {
            project_ids: ids, status,
        });
        return response.data;
    },

    // Project-wide open-feedback queue (#342 Phase 2) — dashboard "needs attention"
    // card. Returns { comments, truncated }.
    //
    // NOT the tree's comment-dot source any more: called once per project it was the
    // second request storm on the requirements view. The dashboard keeps it because it
    // genuinely renders the comment rows, for ONE selected project.
    async getProjectFeedback(projectId, opts = {}) {
        try {
            const { status = 'open', limit = 100 } = opts || {};
            const response = await api.get(`/projects/${projectId}/review-feedback`, {
                params: { status, limit },
            });
            return response.data;
        } catch (error) {
            console.error('Failed to load project feedback:', error);
            throw error;
        }
    },

    // Soft-delete the caller's own open, unreplied comment (#342).
    async deleteComment(commentId) {
        try {
            const response = await api.delete(`/comments/${commentId}`);
            return response.data;
        } catch (error) {
            console.error('Failed to delete comment:', error);
            throw error;
        }
    },

    // Escalate a review comment into a heavyweight change_request (#342 Phase 3).
    // opts: { crClass, cause, reason, title? } → { change_request, comment }.
    async escalateComment(commentId, opts = {}) {
        try {
            const { crClass, cause, reason, title } = opts || {};
            const response = await api.post(`/comments/${commentId}/escalate`, {
                cr_class: crClass, cause, reason,
                ...(title ? { title } : {}),
            });
            return response.data;
        } catch (error) {
            console.error('Failed to escalate comment:', error);
            throw error;
        }
    },

    // action: 'resolve' | 'close' | 'reopen' | 'wont_fix' | 'edit'.
    // opts: { resolution?, body? }.
    async patchComment(commentId, action, opts = {}) {
        try {
            const { resolution, body } = opts || {};
            const response = await api.patch(`/comments/${commentId}`, {
                action,
                ...(resolution !== undefined ? { resolution } : {}),
                ...(body !== undefined ? { body } : {}),
            });
            return response.data;
        } catch (error) {
            console.error('Failed to update comment:', error);
            throw error;
        }
    },

    async bulkAiOperation(containerId, operation, limit = 25) {
        try {
            const response = await api.post(`/items/${containerId}/bulk`, { operation, limit });
            return response.data.data;
        } catch (error) {
            console.error('Failed to run bulk AI operation:', error);
            throw error;
        }
    },

    /**
     * `options` is split into named knobs plus a `...rest` that is handed
     * straight to axios, so the rest half has to carry axios's own config keys —
     * without them `rest` infers as `{}` and every `rest.headers`/`rest.params`
     * write below is an error (#968 Phase B).
     *
     * @param {string} itemId
     * @param {{
     *   projectId?: string,
     *   includeArchived?: boolean,
     *   limit?: number,
     *   offset?: number,
     *   paged?: boolean,
     *   headers?: Record<string, string>,
     *   params?: Record<string, unknown>,
     * }} [options]
     */
    async getChildren(itemId, options = {}) {
        try {
            // Accept a projectId in options to scope the lazy children fetch to one
            // project (X-Project-Id), consistent with getItems/searchItems.
            // #380/#396: limit/offset window the child level (doc-import can leave
            // thousands of flat children under one node); `paged` also returns page meta.
            const { projectId, includeArchived, limit, offset, paged, ...rest } = options || {};
            if (projectId) rest.headers = { ...(rest.headers || {}), 'X-Project-Id': projectId };
            if (includeArchived) rest.params = { ...(rest.params || {}), include_archived: true };
            if (limit != null) rest.params = { ...(rest.params || {}), limit };
            if (offset != null) rest.params = { ...(rest.params || {}), offset };
            const response = await api.get(`/items/${itemId}/children`, rest);
            console.log('Children API Response:', {
                status: response.status,
                data: response.data
            });
            if (paged) return { items: response.data.data || [], page: response.data.page || null };
            return response.data.data;
        } catch (err) {
            console.error('Children API Error:', {
                message: err.message,
                response: err.response?.data,
                rawResponse: err.response,
                status: err.response?.status
            });
            throw err;
        }
    },

    async reviewRequirement(id) {
        try {
            const response = await api.get(`/items/${id}/review`);
            return response.data;
        } catch (error) {
            console.error('Failed to review requirement:', error);
            throw error;
        }
    },

    // Upstream/downstream/verification impact tree for an item (#182).
    // `upDepth` optionally lets the ancestor (upstream) walk go deeper than
    // `depth` without also uncapping the (potentially much wider) downstream/
    // verification walk — see `core.requirements.impact_tree.MAX_UPSTREAM_DEPTH`.
    // Omit it to keep the old byte-for-byte behaviour (upstream mirrors depth).
    async getImpactTree(id, depth = 3, upDepth) {
        try {
            const params = { depth };
            if (upDepth !== undefined) params.up_depth = upDepth;
            const response = await api.get(`/items/${id}/impact-tree`, { params });
            return response.data.data;
        } catch (error) {
            console.error('Failed to load impact tree:', error);
            throw error;
        }
    },

    async checkConsistency(id) {
        try {
            const response = await api.get(`/items/${id}/consistency`);
            return response.data;
        } catch (error) {
            console.error('Failed to check consistency:', error);
            throw error;
        }
    },

    async decomposeRequirement(id) {
        try {
            const response = await api.post(`/items/${id}/decompose`);
            return response.data;
        } catch (error) {
            console.error('Failed to decompose requirement:', error);
            throw error;
        }
    },

    async generateTests(id) {
        try {
            const response = await api.get(`/items/${id}/tests`);
            return response.data;
        } catch (error) {
            console.error('Failed to generate tests:', error);
            throw error;
        }
    },

    async identifyEntities(id) {
        try {
            const response = await api.get(`/items/${id}/entities`);
            return response.data;
        } catch (error) {
            console.error('Failed to identify entities:', error);
            throw error;
        }
    },

    async calculateLimitations(id) {
        try {
            const response = await api.get(`/items/${id}/calculations`);
            return response.data;
        } catch (error) {
            console.error('Failed to calculate limitations:', error);
            throw error;
        }
    },

    async deleteItem(id, reason) {
        try {
            console.log(`Deleting item ${id}`);
            const response = reason
                ? await api.delete(`/items/${id}`, { headers: { 'X-Change-Reason': reason } })
                : await api.delete(`/items/${id}`);
            console.log('Delete item response:', response.data);
            return response.data;
        } catch (error) {
            console.error('Failed to delete item:', error);
            throw error;
        }
    },

    // #193: human-only completion of an agent-proposed archive (MCP `archive_requirement`
    // only PROPOSES — attributes.status becomes 'archive_proposed'). Neither endpoint is
    // reachable from any MCP tool; these are REST-only, normal-auth human actions.
    async confirmArchive(id, reason) {
        try {
            const response = reason
                ? await api.post(`/items/${id}/confirm-archive`, {}, { headers: { 'X-Change-Reason': reason } })
                : await api.post(`/items/${id}/confirm-archive`);
            return response.data;
        } catch (error) {
            console.error('Failed to confirm archive:', error);
            throw error;
        }
    },

    async cancelArchive(id, reason) {
        try {
            const response = reason
                ? await api.post(`/items/${id}/cancel-archive`, {}, { headers: { 'X-Change-Reason': reason } })
                : await api.post(`/items/${id}/cancel-archive`);
            return response.data.data;
        } catch (error) {
            console.error('Failed to cancel archive proposal:', error);
            throw error;
        }
    },

    // #349: flat list of archived (soft-deleted) items in scope, for the Settings
    // "Archived items" recovery list.
    async listArchived(projectId = null) {
        const response = await api.get('/items/archived',
            projectId ? { headers: { 'X-Project-Id': projectId } } : {});
        return response.data.data || [];
    },
    // #349/#658: restore a previously-archived item (reverse of confirm-archive).
    // `options.cascade` (#658) also restores every ARCHIVED descendant — the admin
    // "unarchive all children" option; the response then also carries `cascaded_ids`.
    async unarchiveItem(id, reason, options = {}) {
        const { cascade } = options || {};
        let response;
        if (reason || cascade) {
            const config = {};
            if (reason) config.headers = { 'X-Change-Reason': reason };
            if (cascade) config.params = { cascade: true };
            response = await api.post(`/items/${id}/unarchive`, {}, config);
        } else {
            // Unchanged from before #658: the plain no-reason call stays a single-arg
            // POST (existing tests pin this exact shape).
            response = await api.post(`/items/${id}/unarchive`);
        }
        return cascade ? response.data : response.data.data;
    },
    // #658: preflight count of live descendants a container's archive would
    // cascade to — powers the "This action will also archive all X child items"
    // warning shown before Delete / confirm-archive.
    async getArchiveImpact(id) {
        const response = await api.get(`/items/${id}/archive-impact`);
        return response.data.data;
    },
    // #658: preflight count of archived descendants an "unarchive all children"
    // restore would cascade to.
    async getUnarchiveImpact(id) {
        const response = await api.get(`/items/${id}/unarchive-impact`);
        return response.data.data;
    },

    // F-21 / REQ-6107 (#581, D-01): `setVerified`/`verifyMany` DELETED, not
    // repurposed (owner-decided on #581 — the dashboard's "Verify all" bulk
    // action is removed outright in favour of a future filtered review queue,
    // #146; #582's typed verification record does not inherit this free-text
    // single-field writer). They used to write `attributes.status =
    // 'verified'/'unverified'` — the exact write path #569/#581 retired. No UI
    // may write that attribute as an approval proxy any more.

    // #348: thin bulk wrappers — loop the per-id endpoints (single-accept stays
    // scoped to one id) and report partial failures as { ok, failed }. No new
    // backend/bulk endpoint is introduced.
    async confirmArchiveMany(ids) {
        const ok = [];
        const failed = [];
        for (const id of ids || []) {
            try { await this.confirmArchive(id); ok.push(id); }
            catch (error) { failed.push({ id, error: error && error.message }); }
        }
        return { ok, failed };
    },

    async deleteMultipleItems(ids) {
        if (!ids || ids.length === 0) {
            console.warn('No IDs provided for deletion');
            return { success: false, message: 'No items to delete' };
        }

        console.log(`Deleting ${ids.length} items:`, ids);
        
        const results = {
            success: true,
            deleted: [],
            failed: []
        };
        
        // Delete items sequentially to ensure all requests are processed
        for (const id of ids) {
            try {
                await this.deleteItem(id);
                results.deleted.push(id);
            } catch (error) {
                console.error(`Failed to delete item ${id}:`, error);
                results.failed.push({ id, error: error.message });
                // Continue with other deletions despite failures
            }
        }
        
        results.success = results.failed.length === 0;
        return results;
    },

    async createLink(sourceId, targetId, linkType) {
        try {
            console.log(`Creating link from ${sourceId} to ${targetId} of type ${linkType}`);
            const response = await api.post(`/items/${sourceId}/link`, {
                target_id: targetId,
                link_type: linkType
            });
            return response.data;
        } catch (error) {
            console.error('Failed to create link:', error);
            throw error;
        }
    },

    async deleteLink(sourceId, targetId, linkType) {
        try {
            console.log(`Deleting link from ${sourceId} to ${targetId} of type ${linkType}`);
            const response = await api.delete(`/items/${sourceId}/link/${targetId}`, {
                params: { link_type: linkType }
            });
            return response.data;
        } catch (error) {
            console.error('Failed to delete relationship:', error);
            throw error;
        }
    },

    async getContainersByType() {
        try {
            const response = await api.get('/items', {
                params: { 
                    type: 'container',
                    all_items: true
                }
            });
            return response.data.data;
        } catch (error) {
            console.error('Failed to get containers:', error);
            throw error;
        }
    },

    /**
     * Get all container-type items
     * 
     * @returns {Promise<Array>} Array of container objects
     */
    async getContainers() {
        try {
            // Use the authenticated axios client (a bare fetch sends no bearer /
            // X-Org-Id header and 401s under real auth). The hierarchical list wraps
            // the array in `data`, and a container is flagged by the top-level
            // `type` field (API-created containers carry `type: 'container'`, not
            // `attributes.type`).
            const response = await api.get('/items', { params: { all_items: true } });
            const data = response.data?.data || [];

            const isContainer = (item) =>
                item.type === 'container' || item.attributes?.type === 'container';

            const filterContainers = (items) => {
                if (!items) return [];
                return items.reduce((acc, item) => {
                    const containerChildren = item.children ? filterContainers(item.children) : [];
                    if (isContainer(item)) {
                        acc.push({ ...item, children: containerChildren });
                    } else if (containerChildren.length > 0) {
                        acc.push(...containerChildren);
                    }
                    return acc;
                }, []);
            };

            return filterContainers(data);
        } catch (error) {
            console.error('Error fetching containers:', error);
            throw error;
        }
    },

    // Implement requirement
    async implementRequirement(id) {
        try {
            const response = await api.get(`/items/${id}/implement`);
            return response.data;
        } catch (error) {
            console.error('Failed to implement requirement:', error);
            throw error;
        }
    },

    // Refine implementation
    async refineImplementation(id, message, currentRequirement) {
        try {
            const response = await api.post(`/items/${id}/implement/refine`, {
                message,
                current_requirement: currentRequirement
            });
            return response.data;
        } catch (error) {
            console.error('Failed to refine implementation:', error);
            throw error;
        }
    },

    // F-60 / REQ-6118 (#606, #629): clear ANOTHER reviewer's recorded decision.
    // `reason` is mandatory server-side (400 without); requires the `approval` role
    // (403 without); 404 when that reviewer has no decision to override. Raw JSON
    // convention, same as getReview/setApproval's sibling comments_api.py routes.
    async overrideApproval(itemId, reviewer, reason) {
        try {
            const response = await api.post(`/items/${itemId}/approval/override`, {
                reviewer, reason,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to override approval:', error);
            throw error;
        }
    },

    // F-62 / REQ-6120 (#606, #629): ask `assignee` to review this item — supersedes
    // whatever assignment was outstanding (one current pending assignment per item).
    async assignReview(itemId, assignee, note) {
        try {
            const body = { assignee };
            if (note !== undefined && note !== '') body.note = note;
            const response = await api.post(`/items/${itemId}/review-assignment`, body);
            return response.data;
        } catch (error) {
            console.error('Failed to assign review:', error);
            throw error;
        }
    },

    // F-62 / REQ-6120 (#606, #629): the assignment(s) for this item — pending only,
    // or the full delegation chain with `includeHistory`.
    async listReviewAssignments(itemId, includeHistory = false) {
        try {
            const response = await api.get(`/items/${itemId}/review-assignments`, {
                params: includeHistory ? { include_history: true } : undefined,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to load review assignments:', error);
            throw error;
        }
    },

    // F-62 / REQ-6120 (#606, #629): the CURRENT assignee passes their pending review
    // on. 403 unless the caller IS the assignee — enforced server-side.
    async delegateReview(assignmentId, delegateTo, note) {
        try {
            const body = { delegate_to: delegateTo };
            if (note !== undefined && note !== '') body.note = note;
            const response = await api.post(`/review-assignments/${assignmentId}/delegate`, body);
            return response.data;
        } catch (error) {
            console.error('Failed to delegate review:', error);
            throw error;
        }
    }
};

// Demo data / examples (#361): the import endpoint requires auth (bearer + active
// project). These MUST go through the shared `api` axios instance so `attachAuth`
// adds Authorization/X-Org-Id/X-Project-Id — a bare fetch() sends no bearer and the
// backend rejects it with "missing bearer token".
export const examplesApi = {
    async list() {
        const response = await api.get('/examples');
        return response.data.data || [];
    },
    async import(id) {
        const response = await api.post(`/examples/${id}/import`);
        return response.data;
    }
};

// Document Import feature (#111): upload a document, AI-extract requirements,
// review, commit to the tree, then optionally AI-design a system hierarchy.
export const documentApi = {
    // Stage 1: upload a file; backend extracts plain text (no DB writes).
    async parse(file) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/documents/parse', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },

    // Stage 2: AI-extract candidate requirements from parsed text (staged).
    // Large documents are chunked server-side (#282); `truncated` signals a
    // chunk still hit its output-token budget despite that.
    async extract(text, docName) {
        const response = await api.post('/documents/extract', { text, doc_name: docName });
        return { requirements: response.data.requirements || [], truncated: !!response.data.truncated };
    },

    // Structure-preserving import (#396): parse an app-exported Markdown document
    // back into a nested {title, description, children:[]} tree WITHOUT any AI
    // rewrite, inverting document_export.render_markdown. Returns the tree; commit
    // it with commitDesign (commit_system_design) under a parent / new project.
    async parseStructured(text, docName) {
        const response = await api.post('/documents/parse-structured', { text, doc_name: docName });
        return response.data.system;
    },

    // Stage 4: commit the reviewed requirements under a parent or new container.
    // #396: when creating a NEW container (no parentId → no parent to inherit a
    // project from) an explicit projectId must be sent as X-Project-Id, otherwise the
    // backend fail-closes ("Select a project before creating a requirement."). Only add
    // the header when projectId is set so the no-project call shape is unchanged.
    async commit({ requirements, parentId, newContainerTitle, projectId }) {
        const args = ['/documents/commit', {
            requirements,
            parent_id: parentId || null,
            new_container_title: newContainerTitle || null
        }];
        if (projectId) args.push({ headers: { 'X-Project-Id': projectId } });
        const response = await api.post(...args);
        return response.data;
    },

    // Stage 5: AI-led system design from a set of requirements (staged).
    // #396: the backend now raises 502 when the model errored, but defend against a
    // 200 carrying the legacy error ENVELOPE (no `system` key) so the caller's Alert
    // path always surfaces the failure instead of silently dropping it.
    async design(requirements) {
        const response = await api.post('/documents/design', { requirements });
        const data = response.data || {};
        if (!data.system) {
            const msg = data.consistency || data.detail || data.error || 'AI system design failed.';
            throw new Error(typeof msg === 'string' ? msg : 'AI system design failed.');
        }
        return data.system;
    },

    // Stage 6: commit the AI-proposed (or structure-preserved) system hierarchy.
    // #396: when the parent is a brand-new project root (structure-preserving import
    // into a new project), there is no existing item to inherit a project from, so an
    // explicit X-Project-Id is required — mirroring commit(). Omitted for the normal
    // case (an existing committed parent already resolves its own project).
    async commitDesign(parentId, system, projectId) {
        const args = ['/documents/design/commit', { parent_id: parentId, system }];
        if (projectId) args.push({ headers: { 'X-Project-Id': projectId } });
        const response = await api.post(...args);
        return response.data;
    }
};

// Assumptions management (capability feature D8): list a project's high-level
// assumptions with their blast-radius count, and validate/reject one (which cascades
// rework flags to its dependents). Mirrors the get_assumptions / set_assumption_status
// backend tools. See ASSUMPTIONS_MODEL.md (rmdv-compliance-packs repo, docs/).
export const assumptionsApi = {
    // List the high-level assumptions in a project. Each:
    // { id, display_id, title, assumption_status, assumption_owner, confidence,
    //   blast_radius_count }.
    async getAssumptions(projectId) {
        const response = await api.get(`/projects/${projectId}/assumptions`, {
            headers: { 'X-Project-Id': projectId },
        });
        return response.data.data || [];
    },

    // Set an assumption's status (validated|rejected|pending) with a required reason; the
    // backend cascades rework flags. Returns { id, assumption_status, blast_radius_count,
    // rework_flagged, rework_cleared }.
    async setAssumptionStatus(projectId, id, status, reason) {
        const response = await api.post(
            `/projects/${projectId}/assumptions/${id}/status`,
            { status, reason },
            { headers: { 'X-Project-Id': projectId, 'X-Change-Reason': reason || '' } },
        );
        return response.data.data;
    },
};

// #161: backend-connected heartbeat. A PLAIN axios call (not the shared `api`
// instance) — `/health` needs no bearer/org auth (it's a public liveness probe,
// see main.py) and must stay a cheap, side-effect-free GET: no attachAuth
// interceptor, no request/response console logging, no billing-envelope
// interceptor. Callers (useHeartbeat) treat ANY rejection (timeout, network
// error, non-2xx) as "unreachable" — the response body isn't otherwise used.
export const healthApi = {
    async ping(opts = {}) {
        const { timeout = 5000 } = opts;
        const response = await axios.get(`${API_ROOT_URL}/health`, { timeout });
        return response.data;
    },
};